import { extractGooglePlaceId } from '@vibesites/runtime';

/**
 * Open Google's write-a-review dialog for this property's listing.
 *
 * Never search Google by apartment name. Shared names resolve to a different
 * listing and the review dialog does not open. Prefer a Place ID, then a
 * g.page review link, then the exact Maps listing URL.
 */

function usable(value: string | null | undefined): value is string {
  if (!value) return false;
  const normalized = value.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

function isGoogleHost(hostname: string): boolean {
  const host = hostname.replace(/^www\./, '').toLowerCase();
  return host === 'google.com' || host.endsWith('.google.com') || host === 'g.page' || host === 'goo.gl' || host.endsWith('.goo.gl');
}

function isGoogleUrl(url: string): boolean {
  try {
    return isGoogleHost(new URL(url).hostname);
  } catch {
    return false;
  }
}

function placeIdFromUrl(url: string): string | null {
  return extractGooglePlaceId(url) || url.match(/\b(ChIJ[\w-]{8,})\b/)?.[1] || null;
}

function writeReviewUrl(placeId: string): string {
  return `https://search.google.com/local/writereview?placeid=${encodeURIComponent(placeId)}`;
}

function isAlreadyReviewLink(url: string): boolean {
  return /search\.google\.com\/local\/writereview/i.test(url) || /\/review\/?(?:[?#]|$)/i.test(url);
}

function gpageReviewUrl(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const host = parsed.hostname.replace(/^www\./, '').toLowerCase();
  if (host !== 'g.page') return null;
  const path = parsed.pathname.replace(/\/+$/, '');
  parsed.pathname = path.endsWith('/review') ? path : `${path}/review`;
  parsed.search = '';
  parsed.hash = '';
  return parsed.toString();
}

/** A Maps URL that already identifies one listing, not a name search. */
function isExactListingUrl(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (!isGoogleHost(parsed.hostname)) return false;
  if (parsed.searchParams.get('cid') || parsed.searchParams.get('query_place_id')) return true;
  if (/[?&]q=place_id:/i.test(url)) return true;
  return parsed.pathname.includes('/maps/place/');
}

export function toGoogleWriteReviewUrl(
  businessUrl: string | null | undefined,
  placeUrl?: string | null,
): string | null {
  const business = usable(businessUrl) ? businessUrl.trim() : null;
  const place = usable(placeUrl) ? placeUrl.trim() : null;

  if (business && isAlreadyReviewLink(business)) return business;
  if (place && isAlreadyReviewLink(place)) return place;

  const placeId = (place && placeIdFromUrl(place)) || (business && placeIdFromUrl(business));
  if (placeId) return writeReviewUrl(placeId);

  const gpage = (business && gpageReviewUrl(business)) || (place && gpageReviewUrl(place));
  if (gpage) return gpage;

  if (business && isExactListingUrl(business)) return business;
  if (place && isExactListingUrl(place)) return place;

  if (business && !isGoogleUrl(business)) return business;
  return business;
}
